import React, { useContext, useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  OutlinedInput,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AnnonceContext from '../../../../../contexts/AnnonceContext';

// Composant Select stylisé
const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(27, 159, 170, 0.15)',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(27, 159, 170, 0.25)',
    },
  },
  '& .MuiSelect-icon': {
    color: '#1b9faa',
    transition: 'transform 0.3s ease',
  },
  '&.Mui-focused .MuiSelect-icon': {
    transform: 'rotate(180deg)',
  },
}));

// Composant MenuItem stylisé
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 16px',
  borderRadius: '8px',
  margin: '4px 8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#e3f2fd',
    transform: 'translateX(4px)',
  },
  '&.Mui-selected': {
    backgroundColor: '#1b9faa !important',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#158a94 !important',
    },
  },
}));

export default function SelectField({ name, data, larg = 200, isSecret = false, attributeValue = "" }) {
  const { formAttributes, setFormAttributes } = useContext(AnnonceContext);
  const [sousSelect, setSousSelect] = useState(false);
  const [isChild, setIsChild] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    // Récupérer la valeur depuis formAttributes
    const existingAttribute = formAttributes.find(att => att.attribute === name);
    const currentValue = existingAttribute ? existingAttribute.attributeValue : attributeValue;
    
    if (currentValue) {
      const initialValue = data.find(
        (option) => option.name === currentValue
      );
      setSelectedValue(initialValue || null);
      
      // Si c'est un select avec sous-options, configurer le sous-select
      if (initialValue && initialValue.attributes?.length > 0) {
        setSousSelect(initialValue);
        setIsChild(true);
      }
    } else {
      setSelectedValue(null);
      setSousSelect(false);
      setIsChild(false);
    }
  }, [formAttributes, attributeValue, data, name]);

  data.sort(function (a, b) {
    if (a.sort && b.sort) {
      if (a.sort < b.sort)
        return -1;
      else
        return 1;
    } else {
      if (a.name < b.name) {
        return -1;
      } else {
        return 1;
      }
    }
  });

  return (
    <Stack spacing={3}>
      <FormControl fullWidth>
        <InputLabel 
          sx={{ 
            color: '#666',
            '&.Mui-focused': {
              color: '#1b9faa',
            },
          }}
        >
          {name}
        </InputLabel>
        <StyledSelect
          value={selectedValue?.name || ''}
          onChange={(e) => {
            const val = data.find(option => option.name === e.target.value);
            if (val) {
              if (val.attributes?.length > 0) {
                setSousSelect(val);
                setIsChild(true);
              }
              setSelectedValue(val);
              setFormAttributes((selectAttributes) => [
                ...selectAttributes.filter((att) => att.attribute !== name),
                {
                  attribute: name,
                  attributeValue: val.name,
                  isSecret: isSecret,
                  uiLabel: data.uiLabel,
                },
              ]);
            } else {
              setIsChild(false);
            }
          }}
          label={name}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                '& .MuiMenu-list': {
                  padding: '8px',
                },
              },
            },
          }}
        >
          <StyledMenuItem value="" disabled>
            <Typography variant="body2" color="text.secondary">
              Sélectionner...
            </Typography>
          </StyledMenuItem>
          {data.map((option) => (
            <StyledMenuItem key={option.name} value={option.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="body1">
                  {option.label || option.name}
                </Typography>
                {option.description && (
                  <Chip 
                    label={option.description} 
                    size="small" 
                    sx={{ 
                      ml: 1, 
                      backgroundColor: '#e3f2fd',
                      color: '#1b9faa',
                      fontSize: '0.7rem',
                    }} 
                  />
                )}
              </Box>
            </StyledMenuItem>
          ))}
        </StyledSelect>
      </FormControl>

      {isChild && sousSelect && (
        <FormControl fullWidth>
          <InputLabel 
            sx={{ 
              color: '#666',
              '&.Mui-focused': {
                color: '#1b9faa',
              },
            }}
          >
            Sélection
          </InputLabel>
          <StyledSelect
            onChange={(e) => {
              const val = sousSelect.attributes.find(option => option.name === e.target.value);
              if (val) {
                setFormAttributes((selectAttributes) => [
                  ...selectAttributes.filter((att) => att.attribute !== `${name}option`),
                  {
                    attribute: `${name}option`,
                    attributeValue: val.name,
                    isSecret: isSecret,
                    uiLabel: val.uiLabel,
                  },
                ]);
              }
            }}
            label="Sélection"
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  '& .MuiMenu-list': {
                    padding: '8px',
                  },
                },
              },
            }}
          >
            <StyledMenuItem value="" disabled>
              <Typography variant="body2" color="text.secondary">
                Sélectionner...
              </Typography>
            </StyledMenuItem>
            {sousSelect.attributes.map((option) => (
              <StyledMenuItem key={option.name} value={option.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body1">
                    {option.label || option.name}
                  </Typography>
                  {option.description && (
                    <Chip 
                      label={option.description} 
                      size="small" 
                      sx={{ 
                        ml: 1, 
                        backgroundColor: '#e3f2fd',
                        color: '#1b9faa',
                        fontSize: '0.7rem',
                      }} 
                    />
                  )}
                </Box>
              </StyledMenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      )}
    </Stack>
  );
}
